/**
 * Prompts Page
 *
 * A chat interface for interacting with an LLM
 */

import { useState } from 'react';
import { Send, Sparkles } from 'lucide-react';
import { Button } from '@loader/ui';
import { Textarea } from '@loader/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@loader/ui';
import { ScrollArea } from '@loader/ui';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

/**
 * Main Prompts page component
 */
export function PromptsPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // TODO: Implement general AI chat endpoint - requires backend implementation
    //
    // Note: The backend already has /api/private/versions/chat-schema and
    // /api/private/versions/chat-schema-stream endpoints for table schema assistance,
    // but those are context-specific to versions/tables.
    //
    // For a general chat interface, we need a new endpoint:
    // Backend endpoint: POST /api/private/ai/chat
    // Request: {
    //   conversationHistory: Array<{ role: 'user' | 'assistant', content: string }>,
    //   message: string,
    //   context?: {
    //     tableIds?: string[],
    //     linkedTableIds?: string[],
    //     workspaceId: string
    //   }
    // }
    // Response: {
    //   message: string,
    //   suggestions?: string[], // Optional follow-up suggestions
    //   metadata?: Record<string, unknown> // Optional context for UI
    // }
    //
    // Or for streaming: POST /api/private/ai/chat-stream (SSE endpoint)
    // Same request format, streams text chunks as Server-Sent Events
    //
    // The endpoint should:
    // - Use OpenAI API (already configured in backend - see OPENAI_API_KEY)
    // - Provide workspace context (available tables, linked tables)
    // - Support data analysis questions
    // - Handle follow-up questions with conversation history

    setTimeout(() => {
      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'This is a placeholder response. LLM integration coming soon!',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);
    }, 1000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="absolute inset-4 top-0 flex flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Fixed header */}
        <div className="shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Prompts</h1>
              <p className="text-muted-foreground mt-2">
                Chat with an AI assistant to help analyze your data
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="text-primary h-8 w-8" />
            </div>
          </div>
        </div>

        {/* Chat area */}
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader>
            <CardTitle>Chat</CardTitle>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
            {/* Messages */}
            <ScrollArea className="flex-1 pr-4">
              {messages.length === 0 ? (
                <div className="text-muted-foreground flex h-full items-center justify-center text-center">
                  <div className="max-w-md space-y-2">
                    <Sparkles className="mx-auto h-12 w-12 opacity-50" />
                    <p className="text-lg font-medium">Start a conversation</p>
                    <p className="text-sm">
                      Ask questions about your tables, request analysis, or
                      get help with data transformations.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.role === 'user'
                          ? 'justify-end'
                          : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-4 py-2 ${
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="whitespace-pre-wrap text-sm">
                          {message.content}
                        </p>
                        <p
                          className={`mt-1 text-xs ${
                            message.role === 'user'
                              ? 'text-primary-foreground/70'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-muted max-w-[80%] rounded-lg px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 animate-bounce rounded-full bg-gray-500" />
                          <div className="h-2 w-2 animate-bounce rounded-full bg-gray-500 delay-100" />
                          <div className="h-2 w-2 animate-bounce rounded-full bg-gray-500 delay-200" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input area */}
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message... (Shift+Enter for new line)"
                className="min-h-[60px] max-h-[200px] resize-none"
                disabled={isLoading}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!input.trim() || isLoading}
                size="icon"
                className="h-[60px] w-[60px]"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default PromptsPage;
